import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import { useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import { useEffect } from "react";
import { useBlocker, type Blocker } from "react-router-dom";

/**
 * Keeps unsaved edits from being dropped by leaving the page.
 *
 * Two exits to cover, and they need different means: an in-app navigation is
 * held by React Router's blocker (which only exists under a data router — ours
 * is `createBrowserRouter`), a reload or a closed tab only by `beforeunload`,
 * whose message the browser writes itself.
 *
 * A form that navigates away once saved (an edit page going back to its view)
 * would be stopped by its own guard, since the cache only catches up with the
 * saved values a moment later — so that navigation says so itself:
 * `navigate(path, AFTER_SAVE)`.
 */
export function useLeaveGuard(
  dirty: boolean,
  {
    search = false,
  }: {
    /** Also hold a change of query string: for a form opened by one (`?tool=`). */
    search?: boolean;
  } = {},
) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty &&
      !(nextLocation.state as { afterSave?: boolean } | null)?.afterSave &&
      (currentLocation.pathname !== nextLocation.pathname ||
        (search && currentLocation.search !== nextLocation.search)),
  );

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Still what Chrome and Safari look at before showing their prompt.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // The edits went away while the question was on screen (saved, or reset):
  // there is nothing left to lose, so let the navigation through.
  useEffect(() => {
    if (blocker.state === "blocked" && !dirty) blocker.proceed();
  }, [blocker, dirty]);

  return blocker;
}

/**
 * The guard alone, for a form that has no save bar — one whose fields save
 * themselves, except the ones holding a draft.
 */
export function LeaveGuard({
  dirty,
  search,
}: {
  dirty: boolean;
  search?: boolean;
}) {
  const blocker = useLeaveGuard(dirty, { search });
  return <LeaveDialog blocker={blocker} />;
}

/** « Quitter sans enregistrer ? », while a navigation is held. */
function LeaveDialog({ blocker }: { blocker: Blocker }) {
  const { t } = useTranslation("common");
  return (
    <Dialog
      open={blocker.state === "blocked"}
      onOpenChange={(open) => {
        if (!open && blocker.state === "blocked") blocker.reset();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("common.unsaved.leaveTitle")}</DialogTitle>
          <DialogDescription>
            {t("common.unsaved.leaveDescription")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => blocker.reset?.()}>
            {t("common.unsaved.stay")}
          </Button>
          <Button variant="destructive" onClick={() => blocker.proceed?.()}>
            {t("common.unsaved.leave")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The `navigate` options of a page leaving once its edits are saved. */
export const AFTER_SAVE = { state: { afterSave: true } } as const;

/**
 * Whether a draft kept in `useState` still says what was saved — the `dirty` a
 * hand-written form hands to `FormFooter`. Field by field over both objects, so
 * a key the draft added and one the server returned both count; values are
 * compared by their JSON, which covers the arrays and small objects our
 * entities carry (and treats `undefined` and a missing key alike).
 */
export function differs<T extends object>(
  draft: Partial<T>,
  saved: Partial<T> | undefined,
) {
  if (!saved) return false;
  const keys = new Set([...Object.keys(draft), ...Object.keys(saved)]);
  for (const key of keys) {
    const a = draft[key as keyof T];
    const b = saved[key as keyof T];
    if (a !== b && JSON.stringify(a) !== JSON.stringify(b)) return true;
  }
  return false;
}

/**
 * The save bar of a form: sticky at the bottom of the content panel, shown only
 * while there is something to save — « Modifications non enregistrées ·
 * Annuler · Enregistrer » — and the guard that asks before leaving with it.
 *
 * It replaces six placements of the Save button (top right, bottom left, one
 * per card…), none of them visible once the form was scrolled. It keeps no
 * state of its own: `dirty` is the page's comparison of what is typed with what
 * is saved, whatever it keeps that in — `useState` today, react-hook-form's
 * `formState.isDirty` tomorrow.
 *
 * Render it last inside the page body, so it sticks within the page's width.
 */
export function FormFooter({
  dirty,
  saving = false,
  canSave = true,
  onSave,
  onCancel,
  saveLabel,
  form,
  search = false,
  className,
}: {
  dirty: boolean;
  saving?: boolean;
  /** False while the form is invalid: the bar stays, the button is off. */
  canSave?: boolean;
  onSave: () => unknown;
  /** Puts the saved values back. The button is left out without it. */
  onCancel?: () => void;
  saveLabel?: string;
  /**
   * The id of the `<form>` this bar saves. Its Save button then *is* that form's
   * submit button — wherever the bar sits — so Enter in a field saves, as it did
   * when each form carried its own button.
   */
  form?: string;
  /** Also hold a change of query string: a form opened by one (`?tool=`). */
  search?: boolean;
  className?: string;
}) {
  const { t } = useTranslation("common");
  const blocker = useLeaveGuard(dirty, { search });

  return (
    <>
      {(dirty || saving) && (
        <div
          role="region"
          aria-label={t("common.unsaved.label")}
          className={cn(
            "sticky bottom-4 z-20 mt-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-popover px-4 py-3 text-popover-foreground shadow-overlay",
            "animate-in fade-in slide-in-from-bottom-2 duration-200",
            className,
          )}
        >
          <p className="flex items-center gap-2 text-sm font-medium">
            <span className="size-2 rounded-full bg-warning" aria-hidden />
            {t("common.unsaved.label")}
          </p>
          <div className="ml-auto flex items-center gap-2">
            {onCancel && (
              <Button
                type="button"
                variant="ghost"
                onClick={onCancel}
                disabled={saving}
              >
                {t("common.cancel")}
              </Button>
            )}
            <Button
              {...(form
                ? { type: "submit" as const, form }
                : { type: "button" as const, onClick: () => void onSave() })}
              disabled={saving || !canSave}
            >
              {saving && <Spinner variant="circle" />}
              {saving ? t("common.saving") : saveLabel || t("common.save")}
            </Button>
          </div>
        </div>
      )}

      <LeaveDialog blocker={blocker} />
    </>
  );
}
